<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="確認"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-4">
            <div class="modal-body">
              <v-container fluid class="pa-0">
                <div class="pb-3 pl-2">
                  以下の内容で申し込みます。よろしいですか？<br />
                  帯域確保品目で減速した場合、通信に影響を与える可能性があります。<br />
                  帯域値、QoS設定、VLAN制御を変更した場合、ご請求額が変更となります。
                </div>

                <app-table
                  primary-key="lineSeq"
                  :fields="displayFields"
                  :items="accessLine.items"
                  :per-page="-1"
                  class="mb-4"
                >
                  <!--回線番号 -->
                  <template #cell(enumberAct)="data">
                    {{ data.item.enumberAct }}<br />
                    <template
                      v-if="
                        data.item.menu === 'PF_ACCESS_GW2' &&
                        data.item.enumberSby
                      "
                      >{{ data.item.enumberSby }}
                    </template>
                  </template>

                  <!--メニュー -->
                  <template #cell(menu)="data">
                    <span>{{
                      $filter.enumTo(data.value, "accessLineMenu")
                    }}</span>
                  </template>

                  <!--帯域タイプ-->
                  <template #cell(bandwidthType)="data">
                    <template v-if="data.item.bandwidthType === 'BURST'">
                      <span
                        :class="
                          data.item.bandwidthType !==
                          accessList[data.index].bandwidthType
                            ? 'changed-content'
                            : 'content'
                        "
                        >{{
                          $filter.enumTo(
                            data.item.burst,
                            "accessLineBandwidthType"
                          )
                        }}</span
                      >
                    </template>
                    <template v-else>
                      <span
                        :class="
                          data.item.bandwidthType !==
                          accessList[data.index].bandwidthType
                            ? 'changed-content'
                            : 'content'
                        "
                        >{{
                          $filter.enumTo(
                            data.item.bandwidthType,
                            "accessLineBandwidthType"
                          )
                        }}</span
                      >
                    </template>
                  </template>

                  <!--帯域固定-->
                  <template #cell(fixingBandwidth)="data">
                    <template
                      v-if="
                        data.item.menu === 'ETHERNET2' ||
                        data.item.menu === 'ETHERNET2_PF'
                      "
                    >
                      <span
                        :class="
                          data.item.fixingBandwidth !==
                          accessList[data.index].fixingBandwidth
                            ? 'changed-content'
                            : 'content'
                        "
                        >{{ data.item.fixingBandwidth }}</span
                      >
                    </template>
                    <template v-else> - </template>
                  </template>

                  <!--帯域値-->
                  <template #cell(bandwidth)="data">
                    <template v-if="data.item.bandwidthType === 'BURST'">
                      <span
                        :class="
                          data.item.burst !== accessList[data.index].burst
                            ? 'changed-content'
                            : 'content'
                        "
                      >
                        {{ $filter.optionText(data.item.burst, burstType) }}
                      </span>
                    </template>
                    <template
                      v-else-if="data.item.bandwidthType === 'BESTEFFORT'"
                    >
                      -
                    </template>
                    <template v-else>
                      <span :class="changedBandwidth(data.index)">
                        {{ $filter.bandwidthTo(data.item.bandwidth, "Mbps") }}
                      </span>
                    </template>
                  </template>

                  <!--QoS-->
                  <template #cell(qos)="data">
                    <template
                      v-if="
                        data.item.menu === 'PF_ACCESS_GW' ||
                        data.item.menu === 'EXTEND_ETHERNET' ||
                        data.item.menu === 'BROADBAND_ACCESS3' ||
                        data.item.menu === 'WIRELESS_ACCESS2' ||
                        data.item.menu === 'ETHERNET_LIGHT' ||
                        data.item.menu === 'MULTI_CLOUD_GW' ||
                        (data.item.menu === 'PF_ACCESS_GW2' &&
                          data.item.isQosPath === false)
                      "
                    >
                      -
                    </template>
                    <template v-else>
                      <span
                        :class="
                          data.item.qos !== accessList[data.index].qos
                            ? 'changed-content'
                            : 'content'
                        "
                        >{{ data.item.qos }}</span
                      >
                    </template>
                  </template>

                  <!--VLAN制御-->
                  <template #cell(vlanControl)="data">
                    <template
                      v-if="
                        data.item.menu === 'PF_ACCESS_GW' ||
                        data.item.menu === 'EXTEND_ETHERNET' ||
                        data.item.menu === 'PF_ACCESS_GW2' ||
                        data.item.menu === 'MULTI_CLOUD_GW' ||
                        isEthernet2Broadband(data.item)
                      "
                    >
                      -
                    </template>
                    <template v-else>
                      <span
                        :class="
                          data.item.vlanControl !==
                          accessList[data.index].vlanControl
                            ? 'changed-content'
                            : 'content'
                        "
                        >{{ data.item.vlanControl }}</span
                      >
                    </template>
                  </template>

                  <!--事業所名-->
                  <template #cell(office)="data">
                    <span>{{ $filter.emptyTo(data.value, "-") }}</span>
                  </template>
                </app-table>

                <app-badge
                  item="オーダー備考"
                  :msg="$filter.message('info_max_length', { maxLength: 200 })"
                  :is-info="true"
                  :isRequired="false"
                ></app-badge>
                <validation-provider
                  name="note"
                  label="オーダー備考"
                  v-model="note"
                  :rules="{ max: { length: 200 } }"
                  v-slot="{ componentField, errorMessage, meta }"
                >
                  <app-form-textarea
                    rows="3"
                    v-bind="componentField"
                    :error-messages="errorMessage"
                    :field-meta="meta"
                  ></app-form-textarea>
                </validation-provider>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">はい</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >いいえ</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { BandwidthList } from "@/modals/accessLine/AccessLineModify.vue";
import type { BandwidthListGetEntity } from "@/apis/AccessLineApi";

export default defineComponent({
  name: "AccessLineModifyConfirm",
  props: {
    /** 変更前リスト */
    accessList: {
      type: Array as PropType<BandwidthList[]>,
      required: true,
    },
    /** 変更後リスト */
    modifiedAccessList: {
      type: Array as PropType<BandwidthList[]>,
      required: true,
    },
  },
  data() {
    return {
      accessLine: {
        fields: [
          {
            key: "enumberAct",
            title: "回線番号",
            width: "120px",
          },
          {
            key: "menu",
            title: "メニュー",
            width: "250px",
          },
          {
            key: "bandwidthType",
            title: "帯域タイプ",
            width: "240px",
          },
          {
            key: "fixingBandwidth",
            title: "帯域固定",
            width: "130px",
          },
          {
            key: "bandwidth",
            title: "帯域値",
            width: "170px",
          },
          {
            key: "qos",
            title: "QoS",
            width: "150px",
          },
          {
            key: "vlanControl",
            title: "VLAN制御",
            width: "150px",
          },
          {
            key: "office",
            title: "事業所名",
          },
        ],
        items: [] as BandwidthList[],
      },
      burstType: [
        { value: "BURST10", text: "バースト10" },
        { value: "BURST100", text: "バースト100" },
      ],
      /** オーダー備考 */
      note: null as string | null,
    };
  },
  mounted() {
    this.accessLine.items = this.modifiedAccessList;
  },
  computed: {
    /** 一覧の表示項目 */
    displayFields() {
      if (this.$service.permission.isNormalUser()) {
        /** 法人権限で帯域固定非表示処理 */
        return this.accessLine.fields.filter(
          (v) => (v as { key: string }).key !== "fixingBandwidth"
        );
      }
      return this.accessLine.fields;
    },
  },
  methods: {
    /** 帯域値赤字処理 */
    changedBandwidth(index: number): string {
      return this.accessList[index].bandwidth?.value !==
        this.accessLine.items[index].bandwidth?.value ||
        this.accessList[index].bandwidth?.unit !==
          this.accessLine.items[index].bandwidth?.unit ||
        this.accessList[index].bandwidthType !==
          this.accessLine.items[index].bandwidthType
        ? "changed-content"
        : "content";
    },
    /**
     * ETHERNET2/ETHERNET2_PFで且つ広帯域(10G/100G)の場合、true
     */
    isEthernet2Broadband(item: BandwidthListGetEntity): boolean {
      return (
        (item.menu === "ETHERNET2" || item.menu === "ETHERNET2_PF") &&
        (item.physicalInterface === "IF_10G" ||
          item.physicalInterface === "IF_100G")
      );
    },
    async submit() {
      (
        this.$refs.modal as typeof VueFinalModal & {
          ok: (node: string | null) => void;
        }
      ).ok(this.note ? this.note : null);
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(.changed-content) {
  color: #ff0000;
}
</style>
